import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { styled } from 'styled-components';

import CompassHeader, { PageType } from 'Compass/components/CompassHeader';
import {
  evalCreateDatasetEntry,
  evalDeleteDatasetEntry,
  evalGetRunResults,
  evalListDatasetEntries,
  evalListRuns,
  evalListSeedCandidates,
  evalStartRun,
  evalUpdateDatasetEntry,
} from 'Common/api/DazzleInternalService';

const SUITES = ['INTENT', 'AESTHETIC', 'ORDER_INTENT', 'ORDER_AGENT', 'IDEAS', 'IDEAS_IMAGE'] as const;

interface DatasetEntry {
  entry_id: string;
  suite: string;
  input_data: Record<string, unknown>;
  expected_data: Record<string, unknown>;
  description?: string;
  contributor?: string;
  is_active: boolean;
  image_url?: string;
}

interface EvalRun {
  run_id: string;
  suite: string;
  status: string;
  total_count: number;
  completed_count: number;
  summary_data?: {
    accuracy?: number;
    total?: number;
    correct?: number;
    category_stats?: Record<string, { total: number; correct: number }>;
    prompt_type?: string;
    prompt_text?: string;
  };
  started_ts?: string;
  completed_ts?: string;
  error_message?: string;
}

interface EvalRunResult {
  entry_id: string;
  is_correct: boolean;
  output_data?: Record<string, unknown>;
  scores_data?: Array<{ name: string; score: number; metadata?: Record<string, unknown> }>;
  image_url?: string;
  error_message?: string;
  expected_data?: Record<string, unknown>;
  input_data?: Record<string, unknown>;
  description?: string;
}

type TabType = 'dataset' | 'runs' | 'seed';

const formatTimestamp = (ts: string | undefined) => {
  if (!ts) return '';
  return new Date(ts + 'Z').toLocaleString();
};

const EvalPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramSuite = searchParams.get('suite');
  const paramTab = searchParams.get('tab') as TabType | null;
  const paramRun = searchParams.get('run');

  const suite = paramSuite && (SUITES as readonly string[]).includes(paramSuite) ? paramSuite : 'INTENT';
  const tab: TabType = paramTab && ['dataset', 'runs', 'seed'].includes(paramTab) ? paramTab : 'runs';
  const [enlargedImage, setEnlargedImage] = useState<string | null>(null);

  const updateParams = (updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(updates)) {
        if (v === null) next.delete(k);
        else next.set(k, v);
      }
      return next;
    });
  };

  useEffect(() => {
    if (!enlargedImage) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setEnlargedImage(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [enlargedImage]);

  return (
    <>
      <CompassHeader pageType={PageType.Eval} />
      {enlargedImage && (
        <LightboxOverlay onClick={() => setEnlargedImage(null)}>
          <LightboxImage src={enlargedImage} alt="enlarged" onClick={(e) => e.stopPropagation()} />
        </LightboxOverlay>
      )}
      <PageWrapper>
        <Container>
          <TopBar>
            <SuiteSelect value={suite} onChange={(e) => updateParams({ suite: e.target.value, run: null })}>
              {SUITES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </SuiteSelect>
            <TabBar>
              <TabButton $active={tab === 'dataset'} onClick={() => updateParams({ tab: 'dataset', run: null })}>
                Golden Dataset
              </TabButton>
              <TabButton $active={tab === 'runs'} onClick={() => updateParams({ tab: 'runs' })}>
                Eval Runs
              </TabButton>
              {suite === 'INTENT' && (
                <TabButton $active={tab === 'seed'} onClick={() => updateParams({ tab: 'seed', run: null })}>
                  Seed Data
                </TabButton>
              )}
            </TabBar>
          </TopBar>
          {tab === 'dataset' && <DatasetTab key={suite} suite={suite} onImageClick={setEnlargedImage} />}
          {tab === 'runs' && (
            <RunsTab
              key={suite}
              suite={suite}
              onImageClick={setEnlargedImage}
              initialRunId={paramRun}
              onRunSelect={(runId) => updateParams({ run: runId })}
            />
          )}
          {tab === 'seed' && suite === 'INTENT' && (
            <SeedDataTab key={suite} suite={suite} onImageClick={setEnlargedImage} />
          )}
        </Container>
      </PageWrapper>
    </>
  );
};

// --- Dataset Tab ---

const DatasetTab = ({ suite, onImageClick }: { suite: string; onImageClick: (url: string) => void }) => {
  const [entries, setEntries] = useState<DatasetEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const fetchedRef = useRef<boolean | null>(null);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    const response = await evalListDatasetEntries(suite, 1000);
    if (response) {
      setEntries(response.entries || []);
      setTotalCount(response.total_count || 0);
    } else {
      setError('Failed to load dataset');
    }
    setLoading(false);
  }, [suite]);

  if (fetchedRef.current == null) {
    fetchedRef.current = true;
    void loadEntries();
  }

  if (loading) return <StatusText>Loading...</StatusText>;
  if (error) return <StatusText>{error}</StatusText>;

  const handleEdit = (entry: DatasetEntry) => {
    setEditingId(entry.entry_id);
    const expectedKey = getExpectedKey(suite);
    setEditValue(String(entry.expected_data[expectedKey] ?? ''));
  };

  const handleSave = async (entry: DatasetEntry) => {
    const expectedKey = getExpectedKey(suite);
    const updatedExpected = { ...entry.expected_data, [expectedKey]: editValue };
    await evalUpdateDatasetEntry(entry.entry_id, { expected_data: updatedExpected });
    setEditingId(null);
    void loadEntries();
  };

  const handleDelete = async (entryId: string) => {
    await evalDeleteDatasetEntry(entryId);
    void loadEntries();
  };

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>Golden Dataset ({totalCount} entries)</SectionTitle>
      </SectionHeader>
      <TableContainer>
        <Table>
          <thead>
            <tr>
              {suite === 'INTENT' && (
                <>
                  <Th>Image</Th>
                  <Th>Expected Intent</Th>
                  <Th>Description</Th>
                  <Th>Contributor</Th>
                </>
              )}
              {suite === 'AESTHETIC' && (
                <>
                  <Th>Photo ID</Th>
                  <Th>Expected Score</Th>
                </>
              )}
              {suite === 'ORDER_INTENT' && (
                <>
                  <Th>User Message</Th>
                  <Th>Expected Has Order Intent</Th>
                </>
              )}
              {suite === 'ORDER_AGENT' && (
                <>
                  <Th>Test ID</Th>
                  <Th>Expected Create Order</Th>
                  <Th>Expected Product</Th>
                </>
              )}
              {suite === 'IDEAS' && (
                <>
                  <Th>Title</Th>
                  <Th>Source</Th>
                  <Th>Expected Rating</Th>
                </>
              )}
              {suite === 'IDEAS_IMAGE' && (
                <>
                  <Th>Title</Th>
                  <Th>Image</Th>
                  <Th>Expected Relevance</Th>
                </>
              )}
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.entry_id}>
                {suite === 'INTENT' && (
                  <>
                    <Td>
                      {entry.image_url && (
                        <Thumbnail
                          src={entry.image_url}
                          alt="eval"
                          $clickable
                          tabIndex={0}
                          role="button"
                          onClick={() => onImageClick(entry.image_url!)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onImageClick(entry.image_url!);
                            }
                          }}
                        />
                      )}
                    </Td>
                    <Td>
                      {editingId === entry.entry_id ? (
                        <EditInput value={editValue} onChange={(e) => setEditValue(e.target.value)} />
                      ) : (
                        <ClickToEdit onClick={() => handleEdit(entry)}>
                          {String(entry.expected_data.expected_intent ?? '')}
                        </ClickToEdit>
                      )}
                    </Td>
                    <Td>{entry.description}</Td>
                    <Td>{entry.contributor}</Td>
                  </>
                )}
                {suite === 'AESTHETIC' && (
                  <>
                    <Td>{String(entry.input_data.photo_id ?? '')}</Td>
                    <Td>
                      {editingId === entry.entry_id ? (
                        <EditInput type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)} />
                      ) : (
                        <ClickToEdit onClick={() => handleEdit(entry)}>
                          {String(entry.expected_data.expected_score ?? '')}
                        </ClickToEdit>
                      )}
                    </Td>
                  </>
                )}
                {suite === 'ORDER_INTENT' && (
                  <>
                    <Td>{String(entry.input_data.user_message ?? '').slice(0, 80)}</Td>
                    <Td>
                      {editingId === entry.entry_id ? (
                        <EditSelect value={editValue} onChange={(e) => setEditValue(e.target.value)}>
                          <option value="true">true</option>
                          <option value="false">false</option>
                        </EditSelect>
                      ) : (
                        <ClickToEdit onClick={() => handleEdit(entry)}>
                          {String(entry.expected_data.expected_has_order_intent ?? '')}
                        </ClickToEdit>
                      )}
                    </Td>
                  </>
                )}
                {suite === 'ORDER_AGENT' && (
                  <>
                    <Td>{entry.description}</Td>
                    <Td>
                      {editingId === entry.entry_id ? (
                        <EditSelect value={editValue} onChange={(e) => setEditValue(e.target.value)}>
                          <option value="true">true</option>
                          <option value="false">false</option>
                        </EditSelect>
                      ) : (
                        <ClickToEdit onClick={() => handleEdit(entry)}>
                          {String(entry.expected_data.expected_should_create_order ?? '')}
                        </ClickToEdit>
                      )}
                    </Td>
                    <Td>{String(entry.expected_data.expected_product_name ?? '')}</Td>
                  </>
                )}
                {suite === 'IDEAS' && (
                  <>
                    <Td>{String(entry.input_data.title ?? '')}</Td>
                    <Td>{String(entry.input_data.source ?? '')}</Td>
                    <Td>
                      {editingId === entry.entry_id ? (
                        <EditSelect value={editValue} onChange={(e) => setEditValue(e.target.value)}>
                          <option value="good">good</option>
                          <option value="neutral">neutral</option>
                          <option value="bad">bad</option>
                        </EditSelect>
                      ) : (
                        <ClickToEdit onClick={() => handleEdit(entry)}>
                          {String(entry.expected_data.expected_rating ?? '')}
                        </ClickToEdit>
                      )}
                    </Td>
                  </>
                )}
                {suite === 'IDEAS_IMAGE' && (
                  <>
                    <Td>{String(entry.input_data.title ?? '')}</Td>
                    <Td>
                      {typeof entry.input_data.external_image_url === 'string' && (
                        <Thumbnail
                          src={entry.input_data.external_image_url}
                          alt="idea cover"
                          $clickable
                          tabIndex={0}
                          role="button"
                          onClick={() => onImageClick(entry.input_data.external_image_url as string)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onImageClick(entry.input_data.external_image_url as string);
                            }
                          }}
                        />
                      )}
                    </Td>
                    <Td>
                      {editingId === entry.entry_id ? (
                        <EditSelect value={editValue} onChange={(e) => setEditValue(e.target.value)}>
                          <option value="good">good</option>
                          <option value="bad">bad</option>
                        </EditSelect>
                      ) : (
                        <ClickToEdit onClick={() => handleEdit(entry)}>
                          {String(entry.expected_data.expected_relevance ?? '')}
                        </ClickToEdit>
                      )}
                    </Td>
                  </>
                )}
                <Td>
                  {editingId === entry.entry_id ? (
                    <ActionGroup>
                      <ActionButton onClick={() => handleSave(entry)}>Save</ActionButton>
                      <ActionButton $secondary onClick={() => setEditingId(null)}>
                        Cancel
                      </ActionButton>
                    </ActionGroup>
                  ) : (
                    <ActionButton $danger onClick={() => handleDelete(entry.entry_id)}>
                      Delete
                    </ActionButton>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>
    </Section>
  );
};

// --- Runs Tab ---

const RunsTab = ({
  suite,
  onImageClick,
  initialRunId,
  onRunSelect,
}: {
  suite: string;
  onImageClick: (url: string) => void;
  initialRunId?: string | null;
  onRunSelect?: (runId: string | null) => void;
}) => {
  const [runs, setRuns] = useState<EvalRun[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(initialRunId ?? null);
  const [results, setResults] = useState<EvalRunResult[]>([]);
  const [failuresOnly, setFailuresOnly] = useState(false);
  const [starting, setStarting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fetchedRef = useRef<boolean | null>(null);
  const selectedRunIdRef = useRef(selectedRunId);
  const failuresOnlyRef = useRef(failuresOnly);

  useEffect(() => {
    selectedRunIdRef.current = selectedRunId;
  }, [selectedRunId]);

  useEffect(() => {
    failuresOnlyRef.current = failuresOnly;
  }, [failuresOnly]);

  const loadRuns = useCallback(async () => {
    setError(null);
    const response = await evalListRuns(suite);
    if (response) {
      const runsList = response.runs || [];
      setRuns(runsList);
      if (initialRunId && runsList.some((r: EvalRun) => r.run_id === initialRunId)) {
        setLoadingResults(true);
        const resultsResponse = await evalGetRunResults(initialRunId, false);
        if (resultsResponse) {
          setResults(resultsResponse.results || []);
        }
        setLoadingResults(false);
      }
    } else {
      setError('Failed to load eval runs');
    }
    setLoading(false);
  }, [suite, initialRunId]);

  const wasPollingRef = useRef(false);

  useEffect(() => {
    const hasRunning = runs.some((r) => r.status === 'RUNNING' || r.status === 'PENDING');
    if (!hasRunning) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      if (wasPollingRef.current) {
        wasPollingRef.current = false;
        const currentSelected = selectedRunIdRef.current;
        if (currentSelected) {
          void evalGetRunResults(currentSelected, failuresOnlyRef.current).then((res) => {
            if (res) setResults(res.results || []);
          });
        }
      }
      return;
    }
    wasPollingRef.current = true;
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      const response = await evalListRuns(suite);
      if (!response) return;
      setRuns(response.runs || []);

      const currentSelected = selectedRunIdRef.current;
      if (currentSelected) {
        const activeRun = (response.runs || []).find(
          (r: EvalRun) => r.run_id === currentSelected && r.status === 'RUNNING'
        );
        if (activeRun) {
          const resultsResponse = await evalGetRunResults(currentSelected, failuresOnlyRef.current);
          if (resultsResponse) {
            setResults(resultsResponse.results || []);
          }
        }
      }
    }, 5000);
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [runs, suite]);

  if (fetchedRef.current == null) {
    fetchedRef.current = true;
    void loadRuns();
  }

  if (loading) return <StatusText>Loading...</StatusText>;
  if (error) return <StatusText>{error}</StatusText>;

  const handleStartRun = async () => {
    setStarting(true);
    await evalStartRun(suite);
    setStarting(false);
    const response = await evalListRuns(suite);
    if (response) {
      setRuns(response.runs || []);
    }
  };

  const handleSelectRun = async (runId: string) => {
    if (selectedRunId === runId) {
      setSelectedRunId(null);
      onRunSelect?.(null);
      setResults([]);
      return;
    }
    setSelectedRunId(runId);
    onRunSelect?.(runId);
    setLoadingResults(true);
    const response = await evalGetRunResults(runId, failuresOnly);
    if (response) {
      setResults(response.results || []);
    }
    setLoadingResults(false);
  };

  const handleToggleFailuresOnly = async (checked: boolean) => {
    setFailuresOnly(checked);
    if (selectedRunId) {
      setLoadingResults(true);
      const response = await evalGetRunResults(selectedRunId, checked);
      if (response) {
        setResults(response.results || []);
      }
      setLoadingResults(false);
    }
  };

  const handleFixGolden = async (result: EvalRunResult) => {
    const expectedKey = getExpectedKey(suite);
    const outputKey = getOutputKey(suite);
    const newValue = result.output_data?.[outputKey];
    if (newValue === undefined) return;

    const updatedExpected = { ...result.expected_data, [expectedKey]: newValue };
    await evalUpdateDatasetEntry(result.entry_id, { expected_data: updatedExpected });

    if (selectedRunId) {
      const response = await evalGetRunResults(selectedRunId, failuresOnly);
      if (response) {
        setResults(response.results || []);
      }
    }
  };

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>Eval Runs</SectionTitle>
      </SectionHeader>
      <SectionContent>
        <StartRunBar>
          <StartButton onClick={handleStartRun} disabled={starting}>
            {starting ? 'Starting...' : 'Start Run'}
          </StartButton>
        </StartRunBar>

        {runs.map((run) => (
          <RunCard key={run.run_id}>
            <RunHeader onClick={() => handleSelectRun(run.run_id)}>
              <RunInfo>
                <RunLabel>{run.suite}</RunLabel>
                <RunMeta>
                  <StatusBadge $status={run.status}>{run.status}</StatusBadge>
                  {run.summary_data?.accuracy !== undefined && (
                    <AccuracyBadge>{run.summary_data.accuracy.toFixed(1)}%</AccuracyBadge>
                  )}
                  {run.status === 'RUNNING' && (
                    <ProgressText>
                      {run.completed_count}/{run.total_count}
                    </ProgressText>
                  )}
                </RunMeta>
              </RunInfo>
              <RunHeaderRight>
                <RunTimestamp>{formatTimestamp(run.completed_ts ?? run.started_ts)}</RunTimestamp>
                <CopyLinkButton
                  title="Copy link to run"
                  onClick={(e) => {
                    e.stopPropagation();
                    const url = new URL(window.location.href);
                    url.search = new URLSearchParams({ suite: run.suite, tab: 'runs', run: run.run_id }).toString();
                    void navigator.clipboard.writeText(url.toString());
                  }}
                >
                  Link
                </CopyLinkButton>
              </RunHeaderRight>
            </RunHeader>

            {run.status === 'RUNNING' && run.total_count > 0 && (
              <ProgressBar>
                <ProgressFill style={{ width: `${(run.completed_count / run.total_count) * 100}%` }} />
              </ProgressBar>
            )}

            {selectedRunId === run.run_id && (
              <RunDetails>
                {run.summary_data?.category_stats && (
                  <CategoryStatsTable>
                    <thead>
                      <tr>
                        <Th>Category</Th>
                        <Th>Total</Th>
                        <Th>Correct</Th>
                        <Th>Accuracy</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(run.summary_data.category_stats)
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([cat, stats]) => (
                          <tr key={cat}>
                            <Td>{cat}</Td>
                            <Td>{stats.total}</Td>
                            <Td>{stats.correct}</Td>
                            <Td>{stats.total > 0 ? ((stats.correct / stats.total) * 100).toFixed(1) : 0}%</Td>
                          </tr>
                        ))}
                    </tbody>
                  </CategoryStatsTable>
                )}

                {run.summary_data?.prompt_text && (
                  <PromptSection>
                    <PromptLabel>{run.summary_data.prompt_type ?? 'System Prompt'}</PromptLabel>
                    <PromptBox>{run.summary_data.prompt_text}</PromptBox>
                  </PromptSection>
                )}

                <FilterBar>
                  <label>
                    <input
                      type="checkbox"
                      checked={failuresOnly}
                      onChange={(e) => handleToggleFailuresOnly(e.target.checked)}
                    />{' '}
                    Failures only
                  </label>
                  <ResultCount>{results.length} results</ResultCount>
                </FilterBar>

                {loadingResults ? (
                  <StatusText>Loading results...</StatusText>
                ) : (
                  <ResultsTable>
                    <thead>
                      <tr>
                        {(suite === 'INTENT' || suite === 'AESTHETIC') && <Th>Image</Th>}
                        <Th>Expected</Th>
                        <Th>Predicted</Th>
                        <Th>Result</Th>
                        {suite === 'INTENT' && <Th>Description</Th>}
                        <Th>Actions</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((result, idx) => {
                        const expectedKey = getExpectedKey(suite);
                        const outputKey = getOutputKey(suite);
                        return (
                          <tr key={idx}>
                            {(suite === 'INTENT' || suite === 'AESTHETIC') && (
                              <Td>
                                {result.image_url && (
                                  <Thumbnail
                                    src={result.image_url}
                                    alt="eval"
                                    $clickable
                                    tabIndex={0}
                                    role="button"
                                    onClick={() => onImageClick(result.image_url!)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        onImageClick(result.image_url!);
                                      }
                                    }}
                                  />
                                )}
                              </Td>
                            )}
                            <Td>{String(result.expected_data?.[expectedKey] ?? '')}</Td>
                            <Td>{String(result.output_data?.[outputKey] ?? result.error_message ?? 'ERROR')}</Td>
                            <Td>
                              <ResultBadge $correct={result.is_correct}>
                                {result.is_correct ? 'PASS' : 'FAIL'}
                              </ResultBadge>
                            </Td>
                            {suite === 'INTENT' && <Td>{result.description}</Td>}
                            <Td>
                              {!result.is_correct && (
                                <ActionButton $secondary onClick={() => handleFixGolden(result)}>
                                  Fix Golden
                                </ActionButton>
                              )}
                            </Td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </ResultsTable>
                )}
              </RunDetails>
            )}
          </RunCard>
        ))}
      </SectionContent>
    </Section>
  );
};

// --- Seed Data Tab ---

const INTENT_TYPES = [
  'CALENDAR',
  'CONTACT',
  'COORDINATE',
  'FILE',
  'FIX',
  'FOOD',
  'HEALTH',
  'IDENTIFY',
  'OCCASIONS',
  'PRODUCT',
  'QRCODE',
  'REFERENCE_OR_STATUS',
  'REMIND',
  'SHAREABLE_MOMENT',
  'TRANSCRIBE',
] as const;

interface SeedCandidate {
  photo_id: string;
  photo_url: string;
  photo_ts: string | null;
  s3_key: string;
  context: Record<string, unknown>;
}

const SeedDataTab = ({ suite, onImageClick }: { suite: string; onImageClick: (url: string) => void }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramLid = searchParams.get('seed_user_lid');
  const userLid = paramLid ? parseInt(paramLid, 10) || null : null;
  const [inputValue, setInputValue] = useState('');
  const [candidates, setCandidates] = useState<SeedCandidate[]>([]);

  const [taggedIntents, setTaggedIntents] = useState<Record<string, string>>({});
  const [savedEntryIds, setSavedEntryIds] = useState<Record<string, string>>({});
  const [saveStatus, setSaveStatus] = useState<Record<string, 'saving' | 'saved' | 'failed'>>({});
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [nextCursor, setNextCursor] = useState<number | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [loadedLid, setLoadedLid] = useState<number | null>(null);
  const pageSize = 100;

  const loadCandidates = useCallback(
    async (lid: number) => {
      setLoading(true);
      setError(null);
      const response = await evalListSeedCandidates(suite, lid, pageSize);
      if (response) {
        setCandidates(response.photos || []);
        setHasMore(response.has_more ?? false);
        setNextCursor(response.next_cursor ?? undefined);
      } else {
        setError('Failed to load candidate photos');
      }
      setLoading(false);
    },
    [suite]
  );

  if (userLid && loadedLid !== userLid) {
    setLoadedLid(userLid);
    void loadCandidates(userLid);
  }

  const handleLoadMore = async () => {
    if (!userLid || loadingMore) return;
    setLoadingMore(true);
    const response = await evalListSeedCandidates(suite, userLid, pageSize, nextCursor);
    if (response) {
      const newPhotos = response.photos || [];
      setCandidates((prev) => [...prev, ...newPhotos]);
      setHasMore(response.has_more ?? false);
      setNextCursor(response.next_cursor ?? undefined);
    }
    setLoadingMore(false);
  };

  const handleGo = () => {
    const lid = parseInt(inputValue, 10);
    if (lid) setSearchParams({ seed_user_lid: String(lid) });
  };

  const handleTag = async (candidate: SeedCandidate, intent: string) => {
    setTaggedIntents((prev) => ({ ...prev, [candidate.photo_id]: intent }));
    setSaveStatus((prev) => ({ ...prev, [candidate.photo_id]: 'saving' }));
    const result = await evalCreateDatasetEntry(
      suite,
      { photo_id: candidate.photo_id, s3_key: candidate.s3_key, context: candidate.context },
      { expected_intent: intent }
    );
    if (result?.entry_id) {
      setSavedEntryIds((prev) => ({ ...prev, [candidate.photo_id]: result.entry_id }));
      setSaveStatus((prev) => ({ ...prev, [candidate.photo_id]: 'saved' }));
    } else {
      setSaveStatus((prev) => ({ ...prev, [candidate.photo_id]: 'failed' }));
    }
  };

  const handleRetag = async (candidate: SeedCandidate, intent: string) => {
    const entryId = savedEntryIds[candidate.photo_id];
    if (!entryId) return;
    setTaggedIntents((prev) => ({ ...prev, [candidate.photo_id]: intent }));
    setSaveStatus((prev) => ({ ...prev, [candidate.photo_id]: 'saving' }));
    const result = await evalUpdateDatasetEntry(entryId, { expected_data: { expected_intent: intent } });
    setSaveStatus((prev) => ({ ...prev, [candidate.photo_id]: result ? 'saved' : 'failed' }));
  };

  const handleSelect = (candidate: SeedCandidate, value: string) => {
    if (!value) return;
    if (taggedIntents[candidate.photo_id]) {
      void handleRetag(candidate, value);
    } else {
      void handleTag(candidate, value);
    }
  };

  if (!userLid) {
    return (
      <Section>
        <SectionHeader>
          <SectionTitle>Seed Data</SectionTitle>
        </SectionHeader>
        <SeedUserPrompt>
          <div>Enter a user_lid to load candidate photos:</div>
          <SeedUserInput
            placeholder="e.g. 53"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleGo()}
            autoFocus
          />
          <ActionButton onClick={handleGo}>Load</ActionButton>
        </SeedUserPrompt>
      </Section>
    );
  }

  if (loading) return <StatusText>Loading candidate photos...</StatusText>;
  if (error) return <StatusText>{error}</StatusText>;

  const visible = candidates;

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>
          Seed Data — user {userLid} ({visible.length} candidates)
        </SectionTitle>
      </SectionHeader>
      <TableContainer>
        <Table>
          <thead>
            <tr>
              <Th>Image</Th>
              <Th>Timestamp</Th>
              <Th style={{ maxWidth: 180 }}>Address</Th>
              <Th>Intent</Th>
              <Th style={{ width: 70 }}>Status</Th>
            </tr>
          </thead>
          <tbody>
            {visible.map((candidate) => (
              <tr key={candidate.photo_id}>
                <Td>
                  <Thumbnail
                    src={candidate.photo_url}
                    alt="candidate"
                    $clickable
                    tabIndex={0}
                    role="button"
                    onClick={() => onImageClick(candidate.photo_url)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onImageClick(candidate.photo_url);
                      }
                    }}
                  />
                </Td>
                <Td>{candidate.photo_ts ? new Date(candidate.photo_ts).toLocaleString() : ''}</Td>
                <Td>{String(candidate.context?.address ?? '')}</Td>
                <Td>
                  <EditSelect
                    value={taggedIntents[candidate.photo_id] ?? ''}
                    onChange={(e) => handleSelect(candidate, e.target.value)}
                  >
                    <option value="">Select intent...</option>
                    {INTENT_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </EditSelect>
                </Td>
                <Td>
                  <SeedStatus $status={saveStatus[candidate.photo_id]}>
                    {saveStatus[candidate.photo_id] === 'saving' && 'Saving...'}
                    {saveStatus[candidate.photo_id] === 'saved' && 'Saved'}
                    {saveStatus[candidate.photo_id] === 'failed' && 'Failed'}
                  </SeedStatus>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>
      {hasMore && (
        <LoadMoreBar>
          <ActionButton onClick={handleLoadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading...' : 'Load More'}
          </ActionButton>
        </LoadMoreBar>
      )}
    </Section>
  );
};

// --- Helpers ---

const getExpectedKey = (suite: string): string => {
  switch (suite) {
    case 'INTENT':
      return 'expected_intent';
    case 'AESTHETIC':
      return 'expected_score';
    case 'ORDER_INTENT':
      return 'expected_has_order_intent';
    case 'ORDER_AGENT':
      return 'expected_should_create_order';
    case 'IDEAS':
      return 'expected_rating';
    case 'IDEAS_IMAGE':
      return 'expected_relevance';
    default:
      return 'expected';
  }
};

const getOutputKey = (suite: string): string => {
  switch (suite) {
    case 'INTENT':
      return 'user_intent';
    case 'AESTHETIC':
      return 'score';
    case 'ORDER_INTENT':
      return 'has_order_intent';
    case 'ORDER_AGENT':
      return 'should_create_order';
    case 'IDEAS':
      return 'judge_rating';
    case 'IDEAS_IMAGE':
      return 'predicted_relevance';
    default:
      return 'output';
  }
};

// --- Styled Components ---

const PageWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Container = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SuiteSelect = styled.select`
  min-width: 180px;
  padding: 10px 40px 10px 14px;
  border: 1.5px solid #e4e7ef;
  border-radius: 10px;
  font-size: 14px;
  color: #1a1d2b;
  background: white;
  appearance: none;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #cdd1dc;
  }
  &:focus {
    outline: none;
    border-color: #008aff;
    box-shadow: 0 0 0 3px rgba(0, 138, 255, 0.1);
  }
`;

const TabBar = styled.div`
  display: flex;
  gap: 4px;
  background: #f4f5f9;
  padding: 4px;
  border-radius: 10px;
`;

const TabButton = styled.button<{ $active: boolean }>`
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  background: ${({ $active }) => ($active ? 'white' : 'transparent')};
  color: ${({ $active }) => ($active ? '#1A1D2B' : '#8890A5')};
  box-shadow: ${({ $active }) => ($active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none')};
  transition: all 0.15s ease;
`;

const Section = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid #e4e7ef;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: 16px 24px;
  background: linear-gradient(135deg, #02033b 0%, #743d98 60%, #ac326c 100%);
  color: white;
`;

const SectionTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
`;

const SectionContent = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #f8f9fc;
`;

const StatusText = styled.div`
  padding: 40px;
  text-align: center;
  color: #8890a5;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: left;
  border-bottom: 2px solid #e4e7ef;
  font-weight: 600;
  font-size: 13px;
  color: #6b7085;
  background: #f8f9fc;
`;

const Td = styled.td`
  padding: 10px 16px;
  border-bottom: 1px solid #e4e7ef;
  font-size: 13px;
  color: #1a1d2b;
  vertical-align: middle;
`;

const Thumbnail = styled.img<{ $clickable?: boolean }>`
  max-width: 120px;
  max-height: 80px;
  border-radius: 6px;
  object-fit: cover;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: opacity 0.15s ease;

  &:hover {
    opacity: ${({ $clickable }) => ($clickable ? 0.8 : 1)};
  }

  &:focus {
    outline: 2px solid #008aff;
    outline-offset: 2px;
  }
`;

const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  cursor: pointer;
`;

const LightboxImage = styled.img`
  max-width: 90vw;
  max-height: 90vh;
  border-radius: 12px;
  object-fit: contain;
  cursor: default;
`;

const ClickToEdit = styled.span`
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  font-weight: 500;
  &:hover {
    background: #f4f5f9;
  }
`;

const EditInput = styled.input`
  padding: 6px 10px;
  border: 1.5px solid #008aff;
  border-radius: 8px;
  font-size: 13px;
  width: 100%;
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(0, 138, 255, 0.1);
  }
`;

const EditSelect = styled.select`
  padding: 6px 10px;
  border: 1.5px solid #008aff;
  border-radius: 8px;
  font-size: 13px;
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(0, 138, 255, 0.1);
  }
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 6px;
`;

const ActionButton = styled.button<{ $danger?: boolean; $secondary?: boolean }>`
  padding: 8px 16px;
  border: none;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  background: ${({ $danger, $secondary }) => ($danger ? '#F8D7DA' : $secondary ? '#F4F5F9' : '#008AFF')};
  color: ${({ $danger, $secondary }) => ($danger ? '#721C24' : $secondary ? '#6B7085' : 'white')};

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const StartButton = styled.button`
  padding: 10px 22px;
  background: #008aff;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: #0070d6;
    box-shadow: 0 4px 12px -2px rgba(0, 138, 255, 0.25);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StartRunBar = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const RunCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e4e7ef;
`;

const RunHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #f8f9fc;
  }
`;

const RunInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const RunLabel = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #1a1d2b;
`;

const RunMeta = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StatusBadge = styled.span<{ $status: string }>`
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${({ $status }) => {
    switch ($status) {
      case 'COMPLETED':
        return '#D4EDDA';
      case 'RUNNING':
        return '#D1ECF1';
      case 'PENDING':
        return '#FFF3CD';
      case 'FAILED':
        return '#F8D7DA';
      default:
        return '#E4E7EF';
    }
  }};
  color: ${({ $status }) => {
    switch ($status) {
      case 'COMPLETED':
        return '#155724';
      case 'RUNNING':
        return '#0C5460';
      case 'PENDING':
        return '#856404';
      case 'FAILED':
        return '#721C24';
      default:
        return '#6B7085';
    }
  }};
`;

const AccuracyBadge = styled.span`
  font-weight: 700;
  font-size: 14px;
  color: #1a1d2b;
`;

const ProgressText = styled.span`
  font-size: 12px;
  color: #8890a5;
`;

const RunHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RunTimestamp = styled.div`
  font-size: 12px;
  color: #8890a5;
`;

const CopyLinkButton = styled.button`
  padding: 2px 8px;
  border: 1px solid #e4e7ef;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #8890a5;
  background: white;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: #1a1d2b;
    border-color: #cdd1dc;
  }
`;

const ProgressBar = styled.div`
  height: 4px;
  background: #e4e7ef;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #02033b, #743d98, #ac326c);
  transition: width 0.5s ease;
`;

const RunDetails = styled.div`
  padding: 20px;
  border-top: 1px solid #e4e7ef;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #f8f9fc;
`;

const CategoryStatsTable = styled.table`
  width: auto;
  border-collapse: collapse;
  margin-bottom: 8px;
`;

const PromptSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PromptLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #6b7085;
`;

const PromptBox = styled.pre`
  margin: 0;
  padding: 16px;
  background: white;
  border: 1px solid #e4e7ef;
  border-radius: 8px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  line-height: 1.5;
  color: #1a1d2b;
  white-space: pre-wrap;
  max-height: 300px;
  overflow-y: auto;
`;

const FilterBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: #6b7085;
`;

const ResultCount = styled.span`
  font-weight: 500;
  color: #8890a5;
`;

const ResultsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const ResultBadge = styled.span<{ $correct: boolean }>`
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  background: ${({ $correct }) => ($correct ? '#D4EDDA' : '#F8D7DA')};
  color: ${({ $correct }) => ($correct ? '#155724' : '#721C24')};
`;

const LoadMoreBar = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px;
`;

const SeedStatus = styled.span<{ $status?: 'saving' | 'saved' | 'failed' }>`
  font-size: 12px;
  font-weight: 600;
  min-width: 60px;
  display: inline-block;
  color: ${({ $status }) => {
    switch ($status) {
      case 'saving':
        return '#856404';
      case 'saved':
        return '#155724';
      case 'failed':
        return '#721C24';
      default:
        return 'transparent';
    }
  }};
`;

const SeedUserPrompt = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 12px;
  font-size: 14px;
  color: #6b7085;
`;

const SeedUserInput = styled.input`
  border: 1.5px solid #e4e7ef;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 14px;
  width: 200px;
  outline: none;

  &:focus {
    border-color: #008aff;
    box-shadow: 0 0 0 3px rgba(0, 138, 255, 0.1);
  }
`;

export default EvalPage;
